/**
 * App Links Page - Central hub for all PWA links
 * Shows all available apps with descriptions and quick access
 */

import React, { useState } from 'react';
import { 
  ShoppingCart, 
  UtensilsCrossed, 
  Tablet, 
  LayoutDashboard,
  Globe,
  Package,
  Monitor,
  ExternalLink,
  Copy,
  Check,
  QrCode,
  Settings,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { copyText } from '@/lib/copyText';

// PWA Apps configuration
const PWA_APPS = [
  {
    id: 'pos',
    name: 'Point of Sale',
    shortName: 'POS',
    description: 'Full-featured POS for order entry, payments, and table management',
    subdomain: 'pos',
    icon: ShoppingCart,
    color: 'bg-emerald-600',
    colorLight: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    features: ['Order Entry', 'Table Management', 'Card/Cash Payments', 'Receipt Printing'],
    orientation: 'Landscape',
    idealFor: 'Cashier stations, tablets',
  },
  {
    id: 'kds',
    name: 'Kitchen Display',
    shortName: 'KDS',
    description: 'Real-time kitchen ticket display with bump bar support',
    subdomain: 'kds',
    icon: UtensilsCrossed,
    color: 'bg-orange-600',
    colorLight: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    features: ['Live Ticket Queue', 'Station Filtering', 'Sound Alerts', 'Bump Support'],
    orientation: 'Landscape',
    idealFor: 'Kitchen monitors, line cooks',
  },
  {
    id: 'kiosk',
    name: 'Self-Service Kiosk',
    shortName: 'Kiosk',
    description: 'Customer-facing self-ordering kiosk with payment integration',
    subdomain: 'kiosk',
    icon: Tablet,
    color: 'bg-red-600',
    colorLight: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    features: ['Touch Menu', 'Self-Checkout', 'Loyalty Integration', 'Auto-Reset'],
    orientation: 'Portrait',
    idealFor: 'Self-service stations',
  },
  {
    id: 'admin',
    name: 'Admin Dashboard',
    shortName: 'Admin',
    description: 'Complete back-office management for your restaurant',
    subdomain: 'app',
    icon: LayoutDashboard,
    color: 'bg-blue-600',
    colorLight: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    features: ['Reports', 'Menu Management', 'Staff', 'Inventory'],
    orientation: 'Any',
    idealFor: 'Managers, owners',
  },
  {
    id: 'order',
    name: 'Online Ordering',
    shortName: 'Order',
    description: 'Customer-facing online ordering for pickup and delivery',
    subdomain: 'order',
    icon: Globe,
    color: 'bg-purple-600',
    colorLight: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    features: ['Mobile-First', 'Pickup/Delivery', 'Order Tracking', 'Rewards'],
    orientation: 'Portrait',
    idealFor: 'Customer mobile ordering',
  },
  {
    id: 'expo',
    name: 'Expo Display',
    shortName: 'Expo',
    description: 'Food runner display for order assembly and hand-off',
    subdomain: 'expo',
    icon: Package,
    color: 'bg-green-600',
    colorLight: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    features: ['Ready Orders', 'Hand-off Tracking', 'Order Types', 'Sound Alerts'],
    orientation: 'Landscape',
    idealFor: 'Expo station, food runners',
  },
  {
    id: 'display',
    name: 'Front Desk Display',
    shortName: 'Display',
    description: 'Customer-facing order status display for lobby/counter',
    subdomain: 'display',
    icon: Monitor,
    color: 'bg-cyan-600',
    colorLight: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    features: ['Order Status', 'Wait Times', 'Promotions', 'Announcements'],
    orientation: 'Landscape',
    idealFor: 'Lobby screens, waiting area',
  },
];

// App Card Component
function AppCard({ app, baseDomain, onCopy }) {
  const [copied, setCopied] = useState(false);
  const Icon = app.icon;
  const fullUrl = `https://${app.subdomain}.${baseDomain}`;
  
  const handleCopy = () => {
    copyText(fullUrl, 'Link copied!').then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    onCopy?.(app.id);
  };

  const handleOpen = () => {
    window.open(fullUrl, '_blank');
  };

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border-2 ${app.borderColor} overflow-hidden hover:shadow-lg transition-shadow`}>
      {/* Header */}
      <div className={`${app.color} px-3 sm:px-5 py-3 sm:py-4 flex items-center gap-2 sm:gap-3`}>
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center">
          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-xl font-bold text-white truncate">{app.name}</h3>
          <p className="text-white/80 text-xs sm:text-sm truncate">{app.subdomain}.{baseDomain}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-5">
        <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{app.description}</p>
        
        {/* Features */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {app.features.slice(0, 3).map((feature, idx) => (
            <Badge 
              key={idx} 
              variant="outline" 
              className={`${app.colorLight} ${app.borderColor} text-[10px] sm:text-xs px-1.5 sm:px-2`}
            >
              {feature}
            </Badge>
          ))}
          {app.features.length > 3 && (
            <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5 sm:px-2">
              +{app.features.length - 3}
            </Badge>
          )}
        </div>

        {/* Meta info - hidden on very small screens */}
        <div className="hidden xs:flex items-center gap-3 sm:gap-4 text-[10px] sm:text-sm text-slate-500 dark:text-slate-400 mb-3 sm:mb-4">
          <span className="flex items-center gap-1">
            <Monitor className="w-3 h-3 sm:w-4 sm:h-4" />
            {app.orientation}
          </span>
          <span className="flex items-center gap-1 truncate">
            <Info className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="truncate">{app.idealFor}</span>
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button 
            onClick={handleOpen}
            size="sm"
            className={`flex-1 ${app.color} hover:opacity-90 h-8 sm:h-9 text-xs sm:text-sm`}
          >
            <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
            Open
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleCopy}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            {copied ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AppLinksPage() {
  const [baseDomain, setBaseDomain] = useState('chain29.com');
  const [showConfig, setShowConfig] = useState(false);

  const handleCopyAll = () => {
    const allLinks = PWA_APPS.map(app => 
      `${app.name}: https://${app.subdomain}.${baseDomain}`
    ).join('\n');
    copyText(allLinks, 'All links copied!');
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">App Links</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Access all your restaurant apps from one place
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowConfig(!showConfig)} className="h-8 sm:h-9 text-xs sm:text-sm">
            <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="hidden sm:inline">Configure Domain</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopyAll} className="h-8 sm:h-9 text-xs sm:text-sm">
            <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
            <span className="hidden sm:inline">Copy All</span>
          </Button>
        </div>
      </div>

      {/* Domain Config */}
      {showConfig && (
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-3 sm:p-4">
          <label className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-2 block">
            Base Domain
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={baseDomain}
              onChange={(e) => setBaseDomain(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
              placeholder="yourdomain.com"
            />
            <Button size="sm" onClick={() => setShowConfig(false)}>Save</Button>
          </div>
          <p className="text-[10px] sm:text-xs text-slate-500 mt-2">
            Make sure DNS records are configured for all subdomains
          </p>
        </div>
      )}

      {/* Quick Stats - Mobile: 2x2, Desktop: 4 col */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 sm:p-4 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-emerald-600">7</p>
          <p className="text-xs sm:text-sm text-emerald-600">Total Apps</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">3</p>
          <p className="text-xs sm:text-sm text-blue-600">Staff Apps</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 sm:p-4 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">2</p>
          <p className="text-xs sm:text-sm text-purple-600">Customer Apps</p>
        </div>
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 sm:p-4 text-center">
          <p className="text-2xl sm:text-3xl font-bold text-orange-600">2</p>
          <p className="text-xs sm:text-sm text-orange-600">Display Apps</p>
        </div>
      </div>

      {/* Apps Grid - Mobile: 1 col, Tablet: 2 col, Desktop: 3 col */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {PWA_APPS.map((app) => (
          <AppCard 
            key={app.id} 
            app={app} 
            baseDomain={baseDomain}
          />
        ))}
      </div>

      {/* DNS Setup Info */}
      <div className="bg-slate-100 dark:bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-3 sm:mb-4 flex items-center gap-2">
          <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
          DNS Configuration
        </h2>
        <p className="text-slate-600 dark:text-slate-300 text-sm mb-3 sm:mb-4">
          To enable these apps on your custom domain, add the following DNS records:
        </p>
        <div className="bg-slate-900 rounded-lg p-3 sm:p-4 overflow-x-auto">
          <code className="text-[10px] sm:text-sm text-green-400 whitespace-pre">
            {PWA_APPS.map(app => (
              <div key={app.id} className="mb-1">
                {app.subdomain}.{baseDomain}  CNAME  your-deployment-url.com
              </div>
            ))}
          </code>
        </div>
        <p className="text-[10px] sm:text-sm text-slate-500 dark:text-slate-400 mt-3 sm:mt-4">
          Contact your domain provider or IT team to configure these records.
        </p>
      </div>
    </div>
  );
}
